/**
 * MMO Login Scene - Optimized login screen with immersive visuals
 * Features: Parallax stars, ships, responsive design
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import { MMONetworkManager, MMOEvents } from '../../managers/MMONetworkManager';
import { MMOStateManager } from '../../managers/MMOStateManager';
import { EventBus } from '../../utils/EventBus';
import { I18n } from '../../i18n/I18nService';
import { AudioManager } from '../../managers/AudioManager';
import { getHUDSceneKey } from '../../utils/PlatformRouter';

const API_URL = '/api';
const CONNECTION_WATCHDOG_MS = 45000;

const DEV_AUTO_LOGIN = false;
const DEV_EMAIL = 'player@test.com';
const DEV_PASSWORD = 'test123';

// Styling constants
const LOGO_Y_PERCENT = 0.15;
const FORM_TOP_PERCENT = 30;
const FORM_WIDTH = 440;

// localStorage keys
const STORAGE_REMEMBER_EMAIL = 'stellar_conquest_email';
const STORAGE_REMEMBER_CHECKED = 'stellar_conquest_remember';

// CSS animations (cleaned up - removed unused)
const STYLE_ID = 'mmo-login-styles';
const GLOBAL_STYLES = `
  @keyframes formSlideIn {
    from { opacity: 0; transform: translateX(-50%) translateY(20px); }
    to { opacity: 1; transform: translateX(-50%) translateY(0); }
  }

  @keyframes buttonGlowPulse {
    0%, 100% { box-shadow: 0 0 15px rgba(0, 255, 255, 0.15); }
    50% { box-shadow: 0 0 20px rgba(0, 255, 255, 0.25); }
  }

  @keyframes connectingPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  @keyframes focusPulse {
    0% { box-shadow: 0 0 0 rgba(0, 255, 255, 0); }
    50% { box-shadow: 0 0 20px rgba(0, 255, 255, 0.5), inset 0 0 10px rgba(0, 255, 255, 0.1); }
    100% { box-shadow: 0 0 15px rgba(0, 255, 255, 0.3), inset 0 0 10px rgba(0, 255, 255, 0.05); }
  }

  @keyframes logoGlowPulse {
    0%, 100% { text-shadow: 0 0 8px rgba(0, 255, 255, 0.7), 0 0 20px rgba(0, 255, 255, 0.4), 0 0 35px rgba(0, 102, 255, 0.25); }
    50% { text-shadow: 0 0 12px rgba(0, 255, 255, 0.85), 0 0 28px rgba(0, 255, 255, 0.5), 0 0 45px rgba(0, 102, 255, 0.35); }
  }

  .stellar-logo-text {
    font-family: 'Orbitron', sans-serif;
    font-weight: 900;
    font-size: clamp(26px, 5vw, 54px);
    letter-spacing: 5px;
    text-transform: uppercase;
    color: #00ffff;
    text-shadow: 0 0 8px rgba(0, 255, 255, 0.7), 0 0 20px rgba(0, 255, 255, 0.4), 0 0 35px rgba(0, 102, 255, 0.25);
    animation: logoGlowPulse 3s ease-in-out infinite;
    line-height: 1.1;
    text-align: center;
    margin: 0;
    user-select: none;
    pointer-events: none;
  }

  .stellar-logo-container {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .logo-tagline {
    font-family: 'Share Tech Mono', monospace;
    font-size: 16px;
    color: #a0c0d8;
    letter-spacing: 3px;
    margin-top: 12px;
    text-align: center;
  }

  .logo-tagline .separator {
    color: rgba(0, 255, 255, 0.5);
    margin: 0 8px;
  }

  .logo-tagline .earn {
    color: #00ff88;
  }

  .mmo-input:focus { animation: focusPulse 0.3s ease-out forwards; }
  .mmo-input, .mmo-button, .mmo-link, .mmo-checkbox { outline: none; }
  .mmo-input:focus-visible, .mmo-button:focus-visible, .mmo-link:focus-visible {
    outline: 2px solid #00ffff;
    outline-offset: 2px;
  }


  @media (max-width: 768px) {
    .mmo-login-form {
      top: 25% !important;
      width: 90% !important;
      max-width: 400px !important;
      padding: 25px 20px !important;
    }
    .stellar-logo-text {
      font-size: clamp(24px, 8vw, 48px) !important;
      letter-spacing: 3px !important;
    }
  }

  @media (max-height: 700px) {
    .mmo-login-form {
      top: 22% !important;
    }
  }
`;

export class MMOLoginScene extends Phaser.Scene {
  private mmoNetwork!: MMONetworkManager;
  private mmoState!: MMOStateManager;

  // HTML elements
  private emailInput!: HTMLInputElement;
  private passwordInput!: HTMLInputElement;
  private rememberCheckbox!: HTMLInputElement;
  private loginButton!: HTMLButtonElement;
  private inputContainer!: HTMLDivElement;
  private statusElement!: HTMLDivElement;
  private passwordVisible: boolean = false;

  // Parallax layers (optimized - only updates on mouse move)
  private starLayers: Phaser.GameObjects.Graphics[] = [];
  private starData: { x: number; y: number; size: number; alpha: number }[][] = [];
  private mouseX: number = 0;
  private mouseY: number = 0;
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;
  private parallaxDirty: boolean = true;

  // Login state
  private isLoggingIn: boolean = false;
  private connectionWatchdog: Phaser.Time.TimerEvent | null = null;
  private isTransitioning: boolean = false;
  private sessionToken: string | null = null;
  private originalButtonText: string = '';

  constructor() {
    super({ key: 'MMOLoginScene' });
  }

  init(): void {
    this.mmoNetwork = ServiceLocator.get<MMONetworkManager>(Services.MMO_NETWORK);
    this.mmoState = ServiceLocator.get<MMOStateManager>(Services.MMO_STATE);
    this.passwordVisible = false;
    this.starLayers = [];
    this.starData = [];
    this.parallaxDirty = true;
    this.lastMouseX = 0;
    this.lastMouseY = 0;
  }

  create(): void {
    const { width, height } = this.cameras.main;

    this.injectGlobalStyles();

    // Background
    if (this.textures.exists('bg-starfield')) {
      const bg = this.add.image(width / 2, height / 2, 'bg-starfield');
      bg.setDisplaySize(width, height);
      bg.setDepth(-10);
    }

    // Create parallax star layers
    this.createParallaxStars(width, height);

    // Planet removed per user request

    // Ship silhouettes
    this.createShipSpawner(width, height);

    // Shooting stars
    this.createShootingStars(width, height);

    // Logo
    this.createLogo(width, height);

    // HUD data removed per user request

    // Login form
    this.createLoginForm(width, height);

    // Version info
    this.add.text(width / 2, height - 30, I18n.t('login.version'), {
      fontFamily: 'Share Tech Mono, monospace',
      fontSize: '12px',
      color: '#4a6a8a',
    }).setOrigin(0.5).setAlpha(0.6);

    // Mouse tracking for parallax (only mark dirty when mouse actually moves)
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      const newX = (pointer.x - width / 2) / width;
      const newY = (pointer.y - height / 2) / height;
      // Only update if mouse moved significantly (reduces redraws)
      if (Math.abs(newX - this.lastMouseX) > 0.001 || Math.abs(newY - this.lastMouseY) > 0.001) {
        this.mouseX = newX;
        this.mouseY = newY;
        this.lastMouseX = newX;
        this.lastMouseY = newY;
        this.parallaxDirty = true;
      }
    });

    // Update loop for parallax (optimized - only redraws when needed)
    this.events.on('update', this.updateParallax, this);

    this.setupEventListeners();
    this.events.on('shutdown', this.shutdown, this);
    this.cameras.main.fadeIn(300, 0, 0, 0);
    this.loadRememberedEmail();

    if (DEV_AUTO_LOGIN) {
      this.emailInput.value = DEV_EMAIL;
      this.passwordInput.value = DEV_PASSWORD;
      this.time.delayedCall(500, () => this.handleLogin());
    }

    // Auto-login: if a stored token exists, validate and reconnect
    this.tryAutoLogin();
  }

  private injectGlobalStyles(): void {
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = GLOBAL_STYLES;
      document.head.appendChild(style);
    }
  }

  private createParallaxStars(width: number, height: number): void {
    const layerConfigs = [
      { count: 50, speedFactor: 0.02, sizeRange: [0.5, 1.5], alpha: 0.25 },
      { count: 30, speedFactor: 0.05, sizeRange: [1, 2], alpha: 0.4 },
      { count: 18, speedFactor: 0.1, sizeRange: [1.5, 3], alpha: 0.7 },
    ];

    layerConfigs.forEach((config, index) => {
      const graphics = this.add.graphics();
      graphics.setDepth(-5 + index);
      this.starLayers.push(graphics);

      const stars: { x: number; y: number; size: number; alpha: number }[] = [];
      for (let i = 0; i < config.count; i++) {
        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          size: config.sizeRange[0] + Math.random() * (config.sizeRange[1] - config.sizeRange[0]),
          alpha: config.alpha * (0.5 + Math.random() * 0.5),
        });
      }
      this.starData.push(stars);
    });
  }

  private updateParallax(): void {
    // Skip redraw if nothing changed (major performance optimization)
    if (!this.parallaxDirty) return;
    if (!this.cameras?.main) return;
    this.parallaxDirty = false;

    const { width, height } = this.cameras.main;
    const speedFactors = [0.02, 0.05, 0.1];

    this.starLayers.forEach((graphics, layerIndex) => {
      graphics.clear();
      const stars = this.starData[layerIndex];
      const factor = speedFactors[layerIndex];

      stars.forEach((star) => {
        const offsetX = this.mouseX * factor * 100;
        const offsetY = this.mouseY * factor * 100;
        let drawX = star.x + offsetX;
        let drawY = star.y + offsetY;

        // Wrap around
        if (drawX < 0) drawX += width;
        if (drawX > width) drawX -= width;
        if (drawY < 0) drawY += height;
        if (drawY > height) drawY -= height;

        graphics.fillStyle(0xffffff, star.alpha);
        graphics.fillCircle(drawX, drawY, star.size);
      });
    });
  }

  private activeShips: number = 0;
  private readonly MAX_SHIPS = 3;

  private createShipSpawner(width: number, height: number): void {
    // Spawn ships periodically, checking max limit
    this.time.addEvent({
      delay: 4000,
      callback: () => {
        if (this.activeShips < this.MAX_SHIPS) {
          this.spawnShip(width, height);
        }
      },
      loop: true,
    });
    // Initial ships with staggered timing
    this.time.delayedCall(1000, () => this.spawnShip(width, height));
    this.time.delayedCall(3000, () => {
      if (this.activeShips < this.MAX_SHIPS) this.spawnShip(width, height);
    });
  }

  private spawnShip(width: number, height: number): void {
    if (this.activeShips >= this.MAX_SHIPS) return;
    this.activeShips++;

    const ship = this.add.graphics();
    ship.setDepth(-2);

    // Random direction: left-to-right or right-to-left
    const goingRight = Math.random() > 0.5;
    const startX = goingRight ? -30 : width + 30;
    const endX = goingRight ? width + 30 : -30;
    const startY = Phaser.Math.Between(50, height * 0.4);
    const endY = startY + Phaser.Math.Between(-50, 100);

    // Random ship type (0-3)
    const shipType = Phaser.Math.Between(0, 3);

    // Draw ship silhouette based on type and direction
    const drawShip = (x: number, y: number, alpha: number) => {
      ship.clear();
      ship.fillStyle(0x3a5a7a, alpha);
      // Direction multiplier: 1 for right-facing, -1 for left-facing
      const d = goingRight ? 1 : -1;

      switch (shipType) {
        case 0: // Fighter - small, fast
          ship.fillTriangle(x, y, x - 12 * d, y + 4, x - 12 * d, y - 4);
          ship.fillTriangle(x - 8 * d, y - 3, x - 14 * d, y - 8, x - 14 * d, y - 3);
          ship.fillTriangle(x - 8 * d, y + 3, x - 14 * d, y + 8, x - 14 * d, y + 3);
          ship.fillStyle(0x00aaff, alpha * 0.8);
          ship.fillCircle(x - 13 * d, y, 2);
          break;

        case 1: // Cruiser - larger, bulkier
          ship.fillRect(goingRight ? x - 20 : x, y - 3, 20, 6);
          ship.fillTriangle(x, y, x - 5 * d, y - 5, x - 5 * d, y + 5);
          ship.fillRect(goingRight ? x - 18 : x + 10, y - 6, 8, 3);
          ship.fillRect(goingRight ? x - 18 : x + 10, y + 3, 8, 3);
          ship.fillStyle(0x00aaff, alpha * 0.8);
          ship.fillCircle(x - 21 * d, y - 1, 2);
          ship.fillCircle(x - 21 * d, y + 1, 2);
          break;

        case 2: // Freighter - big cargo ship
          ship.fillRect(goingRight ? x - 25 : x + 3, y - 5, 22, 10);
          ship.fillTriangle(x - 3 * d, y, x - 8 * d, y - 6, x - 8 * d, y + 6);
          ship.fillRect(goingRight ? x - 22 : x + 16, y - 8, 6, 3);
          ship.fillRect(goingRight ? x - 22 : x + 16, y + 5, 6, 3);
          ship.fillStyle(0xffaa00, alpha * 0.6);
          ship.fillCircle(x - 26 * d, y - 2, 2);
          ship.fillCircle(x - 26 * d, y + 2, 2);
          break;

        case 3: // Interceptor - sleek, angular
          ship.fillTriangle(x, y, x - 18 * d, y + 2, x - 18 * d, y - 2);
          ship.fillTriangle(x - 10 * d, y - 2, x - 20 * d, y - 7, x - 20 * d, y - 2);
          ship.fillTriangle(x - 10 * d, y + 2, x - 20 * d, y + 7, x - 20 * d, y + 2);
          ship.fillStyle(0xff4466, alpha * 0.8);
          ship.fillCircle(x - 18 * d, y, 1.5);
          break;
      }
    };

    this.tweens.add({
      targets: { progress: 0 },
      progress: 1,
      duration: Phaser.Math.Between(8000, 14000),
      ease: 'Linear',
      onUpdate: (tween: Phaser.Tweens.Tween) => {
        const p = tween.getValue() ?? 0;
        const x = startX + (endX - startX) * p;
        const y = startY + (endY - startY) * p;
        drawShip(x, y, 0.6);
      },
      onComplete: () => {
        ship.destroy();
        this.activeShips--;
      },
    });
  }

  private logoContainer: HTMLDivElement | null = null;

  private createLogo(_width: number, height: number): void {
    const logoY = height * LOGO_Y_PERCENT;

    // Create HTML-based glowing text logo
    this.logoContainer = document.createElement('div');
    this.logoContainer.className = 'stellar-logo-container';
    this.logoContainer.style.top = `${logoY - 40}px`;

    const stellarText = document.createElement('h1');
    stellarText.className = 'stellar-logo-text';
    stellarText.textContent = I18n.t('login.brand_crimson');

    const conquestText = document.createElement('h1');
    conquestText.className = 'stellar-logo-text';
    conquestText.style.marginTop = '-10px';
    conquestText.textContent = I18n.t('login.brand_mandate');

    // Tagline under logo
    const tagline = document.createElement('div');
    tagline.className = 'logo-tagline';
    tagline.textContent = I18n.t('login.tagline');

    this.logoContainer.appendChild(stellarText);
    this.logoContainer.appendChild(conquestText);
    this.logoContainer.appendChild(tagline);
    document.body.appendChild(this.logoContainer);

    // Clean up on scene shutdown
    this.events.once('shutdown', () => {
      if (this.logoContainer) {
        this.logoContainer.remove();
        this.logoContainer = null;
      }
    });
  }

  private createShootingStars(width: number, height: number): void {
    this.time.addEvent({
      delay: 4000,
      callback: () => this.spawnShootingStar(width, height),
      loop: true,
    });
    this.time.delayedCall(2000, () => this.spawnShootingStar(width, height));
  }

  private spawnShootingStar(width: number, height: number): void {
    const star = this.add.graphics();
    star.setDepth(1);

    const fromRight = Math.random() > 0.5;
    const startX = fromRight ? width + 50 : Phaser.Math.Between(100, width);
    const startY = fromRight ? Phaser.Math.Between(0, height * 0.5) : -50;
    const endX = startX - Phaser.Math.Between(300, 600);
    const endY = startY + Phaser.Math.Between(200, 400);
    const length = Phaser.Math.Between(40, 100);
    const alpha = Phaser.Math.FloatBetween(0.3, 0.6);

    this.tweens.add({
      targets: { progress: 0 },
      progress: 1,
      duration: Phaser.Math.Between(1500, 2500),
      ease: 'Cubic.easeIn',
      onUpdate: (tween: Phaser.Tweens.Tween) => {
        const p = tween.getValue() ?? 0;
        const currentX = startX + (endX - startX) * p;
        const currentY = startY + (endY - startY) * p;
        star.clear();

        const angle = Math.atan2(endY - startY, endX - startX);
        const tailX = currentX - Math.cos(angle) * length;
        const tailY = currentY - Math.sin(angle) * length;

        for (let i = 0; i < 5; i++) {
          const t = i / 5;
          const segStartX = tailX + (currentX - tailX) * t;
          const segStartY = tailY + (currentY - tailY) * t;
          const segEndX = tailX + (currentX - tailX) * (t + 0.25);
          const segEndY = tailY + (currentY - tailY) * (t + 0.25);
          star.lineStyle(2 - i * 0.3, 0xffffff, alpha * (1 - t * 0.7));
          star.moveTo(segStartX, segStartY);
          star.lineTo(segEndX, segEndY);
          star.strokePath();
        }
        star.fillStyle(0xffffff, alpha);
        star.fillCircle(currentX, currentY, 2);
      },
      onComplete: () => star.destroy(),
    });
  }

  private createLoginForm(_width: number, _height: number): void {
    this.inputContainer = document.createElement('div');
    this.inputContainer.className = 'mmo-login-form';
    this.inputContainer.style.cssText = `
      position: absolute;
      left: 50%;
      top: ${FORM_TOP_PERCENT}%;
      transform: translateX(-50%);
      width: ${FORM_WIDTH}px;
      max-width: calc(100vw - 40px);
      z-index: 1000;
      font-family: 'Share Tech Mono', monospace;
      padding: 28px 36px;
      background: rgba(3, 10, 22, 0.55);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      border: 1px solid rgba(0, 255, 255, 0.2);
      box-shadow: inset 0 0 30px rgba(0, 255, 255, 0.03), 0 0 20px rgba(0, 0, 0, 0.5);
      opacity: 0;
      animation: formSlideIn 0.4s ease-out 0.2s forwards;
      overflow: hidden;
    `;


    // Email
    const emailLabel = document.createElement('div');
    emailLabel.textContent = I18n.t('login.enter_email');
    emailLabel.style.cssText = `color: #9ac0dc; font-size: 16px; margin-bottom: 10px; letter-spacing: 2px;`;

    this.emailInput = document.createElement('input');
    this.emailInput.type = 'email';
    this.emailInput.placeholder = I18n.t('login.email_placeholder');
    this.emailInput.className = 'mmo-input';
    this.emailInput.tabIndex = 1;
    this.emailInput.style.cssText = `
      width: 100%; padding: 16px 18px; font-size: 18px;
      font-family: 'Share Tech Mono', monospace;
      background: rgba(0, 20, 40, 0.7); color: #ffffff;
      border: 2px solid rgba(0, 255, 255, 0.5); border-radius: 0;
      margin-bottom: 22px; box-sizing: border-box; outline: none;
      transition: border-color 0.2s;
    `;
    this.emailInput.addEventListener('focus', () => this.emailInput.style.borderColor = '#00ffff');
    this.emailInput.addEventListener('blur', () => this.emailInput.style.borderColor = 'rgba(0, 255, 255, 0.5)');
    this.emailInput.addEventListener('keypress', (e) => { if (e.key === 'Enter') this.passwordInput.focus(); });

    // Password
    const passwordLabel = document.createElement('div');
    passwordLabel.textContent = I18n.t('login.enter_password');
    passwordLabel.style.cssText = `color: #9ac0dc; font-size: 16px; margin-bottom: 10px; letter-spacing: 2px;`;

    const passwordContainer = document.createElement('div');
    passwordContainer.style.cssText = `position: relative; margin-bottom: 18px;`;

    this.passwordInput = document.createElement('input');
    this.passwordInput.type = 'password';
    this.passwordInput.placeholder = I18n.t('login.password_placeholder');
    this.passwordInput.className = 'mmo-input';
    this.passwordInput.tabIndex = 2;
    this.passwordInput.style.cssText = `
      width: 100%; padding: 16px 50px 16px 18px; font-size: 18px;
      font-family: 'Share Tech Mono', monospace;
      background: rgba(0, 20, 40, 0.7); color: #ffffff;
      border: 2px solid rgba(0, 255, 255, 0.5); border-radius: 0;
      box-sizing: border-box; outline: none; transition: border-color 0.2s;
    `;
    this.passwordInput.addEventListener('focus', () => this.passwordInput.style.borderColor = '#00ffff');
    this.passwordInput.addEventListener('blur', () => this.passwordInput.style.borderColor = 'rgba(0, 255, 255, 0.5)');
    this.passwordInput.addEventListener('keypress', (e) => { if (e.key === 'Enter' && !this.isLoggingIn) this.handleLogin(); });

    // Toggle
    const toggleBtn = document.createElement('button');
    toggleBtn.type = 'button';
    toggleBtn.innerHTML = '<span style="position:relative;">👁</span>';
    toggleBtn.className = 'mmo-button';
    toggleBtn.tabIndex = 3;
    toggleBtn.style.cssText = `
      position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
      background: none; border: none; color: #6a9aba; font-size: 18px;
      cursor: pointer; padding: 8px; transition: color 0.2s;
    `;
    const updateToggleIcon = (visible: boolean) => {
      toggleBtn.innerHTML = visible
        ? '<span style="position:relative;display:inline-block;">👁<span style="position:absolute;left:50%;top:45%;width:120%;height:2px;background:#6a9aba;transform:translate(-50%,-50%) rotate(-45deg);"></span></span>'
        : '<span style="position:relative;">👁</span>';
    };
    toggleBtn.addEventListener('click', () => {
      this.passwordVisible = !this.passwordVisible;
      this.passwordInput.type = this.passwordVisible ? 'text' : 'password';
      updateToggleIcon(this.passwordVisible);
      toggleBtn.style.color = this.passwordVisible ? '#00ffff' : '#6a9aba';
    });
    toggleBtn.addEventListener('mouseenter', () => toggleBtn.style.color = '#00ffff');
    toggleBtn.addEventListener('mouseleave', () => toggleBtn.style.color = this.passwordVisible ? '#00ffff' : '#6a9aba');

    passwordContainer.appendChild(this.passwordInput);
    passwordContainer.appendChild(toggleBtn);

    // Remember me
    const rememberContainer = document.createElement('label');
    rememberContainer.style.cssText = `display: flex; align-items: center; margin-bottom: 22px; cursor: pointer; padding: 6px 0;`;

    this.rememberCheckbox = document.createElement('input');
    this.rememberCheckbox.type = 'checkbox';
    this.rememberCheckbox.className = 'mmo-checkbox';
    this.rememberCheckbox.tabIndex = 4;
    this.rememberCheckbox.style.cssText = `width: 22px; height: 22px; margin-right: 12px; accent-color: #00ffff; cursor: pointer;`;

    const rememberLabel = document.createElement('span');
    rememberLabel.textContent = I18n.t('login.remember_me');
    rememberLabel.style.cssText = `color: #9ac0dc; font-size: 15px; letter-spacing: 2px;`;

    rememberContainer.appendChild(this.rememberCheckbox);
    rememberContainer.appendChild(rememberLabel);

    // Button
    this.loginButton = document.createElement('button');
    this.originalButtonText = I18n.t('login.conquer_galaxy');
    this.loginButton.innerHTML = this.originalButtonText;
    this.loginButton.className = 'mmo-button';
    this.loginButton.tabIndex = 5;
    this.loginButton.style.cssText = `
      width: 100%; padding: 14px 24px; font-size: 17px;
      font-family: 'Orbitron', sans-serif; font-weight: 700;
      background: rgba(0, 50, 70, 0.9); color: #00ffff;
      border: 2px solid #00ffff; border-radius: 0; cursor: pointer;
      letter-spacing: 3px; line-height: 1.4; transition: background 0.2s, transform 0.2s;
      animation: buttonGlowPulse 2s ease-in-out infinite; margin-bottom: 14px;
    `;
    this.loginButton.addEventListener('mouseenter', () => {
      if (!this.isLoggingIn) {
        this.loginButton.style.background = 'rgba(0, 255, 255, 0.2)';
        this.loginButton.style.transform = 'translateY(-2px)';
      }
    });
    this.loginButton.addEventListener('mouseleave', () => {
      if (!this.isLoggingIn) {
        this.loginButton.style.background = 'rgba(0, 50, 70, 0.9)';
        this.loginButton.style.transform = 'translateY(0)';
      }
    });
    this.loginButton.addEventListener('click', () => { if (!this.isLoggingIn) this.handleLogin(); });

    // Status
    const statusDiv = document.createElement('div');
    statusDiv.style.cssText = `text-align: center; font-size: 15px; color: #ff6666; margin-bottom: 20px; min-height: 22px; display: none;`;
    this.statusElement = statusDiv;

    // Footer
    const footerLinks = document.createElement('div');
    footerLinks.style.cssText = `display: flex; justify-content: space-between; align-items: center; padding-top: 10px; border-top: 1px solid rgba(0, 255, 255, 0.1);`;

    const forgotLink = document.createElement('a');
    forgotLink.textContent = I18n.t('login.forgot_password');
    forgotLink.href = '#';
    forgotLink.className = 'mmo-link';
    forgotLink.tabIndex = 6;
    forgotLink.style.cssText = `color: #7aa0c0; font-size: 15px; text-decoration: none; letter-spacing: 1px; transition: color 0.2s; padding: 8px 0;`;
    forgotLink.addEventListener('mouseenter', () => forgotLink.style.color = '#00ffff');
    forgotLink.addEventListener('mouseleave', () => forgotLink.style.color = '#7aa0c0');
    forgotLink.addEventListener('click', (e) => {
      e.preventDefault();
      const email = this.emailInput?.value?.trim();
      if (!email) {
        this.updateStatus('Enter your email above, then click Forgot Password.');
        return;
      }
      forgotLink.textContent = 'Sending...';
      fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
        .then(r => r.json())
        .then(() => {
          this.updateStatus('Reset link sent! Check your email inbox.');
          forgotLink.textContent = 'Reset link sent';
        })
        .catch(() => {
          this.updateStatus('Failed to send reset email. Try again.');
          forgotLink.textContent = I18n.t('login.forgot_password');
        });
    });

    const createLink = document.createElement('a');
    createLink.textContent = I18n.t('login.create_account');
    createLink.href = '#';
    createLink.className = 'mmo-link';
    createLink.tabIndex = 7;
    createLink.style.cssText = `color: #7aa0c0; font-size: 15px; text-decoration: none; letter-spacing: 1px; transition: color 0.2s; padding: 8px 0;`;
    createLink.addEventListener('mouseenter', () => createLink.style.color = '#00ffff');
    createLink.addEventListener('mouseleave', () => createLink.style.color = '#7aa0c0');
    createLink.addEventListener('click', (e) => {
      e.preventDefault();
      this.cameras.main.fadeOut(200, 0, 0, 0);
      this.time.delayedCall(200, () => { this.cleanupInputs(); this.scene.start('MMORegisterScene'); });
    });

    footerLinks.appendChild(forgotLink);
    footerLinks.appendChild(createLink);

    // Purchase context banner when coming from whitepaper (?shop=true)
    if (new URLSearchParams(window.location.search).get('shop') === 'true') {
      const shopBanner = document.createElement('div');
      shopBanner.style.cssText = `
        padding: 12px 16px; margin-bottom: 18px;
        background: rgba(0, 20, 40, 0.7);
        border: 1px solid rgba(0, 255, 255, 0.4);
        color: #9ac0dc; font-size: 14px; line-height: 1.5;
        letter-spacing: 1px; text-align: center;
      `;
      shopBanner.textContent = I18n.t('login.shop_banner');
      this.inputContainer.appendChild(shopBanner);
    }

    // Assemble (no header - logo is the hero)
    this.inputContainer.appendChild(emailLabel);
    this.inputContainer.appendChild(this.emailInput);
    this.inputContainer.appendChild(passwordLabel);
    this.inputContainer.appendChild(passwordContainer);
    this.inputContainer.appendChild(rememberContainer);
    this.inputContainer.appendChild(this.loginButton);
    this.inputContainer.appendChild(statusDiv);
    this.inputContainer.appendChild(footerLinks);

    document.body.appendChild(this.inputContainer);

    // Audio mute/volume control
    this.createAudioControl();

    // Auto-focus email field immediately after form animation completes
    this.time.delayedCall(250, () => this.emailInput.focus());
  }

  private audioControl: HTMLDivElement | null = null;

  private createAudioControl(): void {
    const audio = ServiceLocator.get<AudioManager>(Services.AUDIO);
    let isMuted = audio.getMasterVolume() === 0;

    const container = document.createElement('div');
    container.style.cssText = `
      position: fixed; bottom: 20px; right: 20px; z-index: 9999;
      display: flex; align-items: center; gap: 8px;
    `;

    const muteBtn = document.createElement('button');
    muteBtn.type = 'button';
    muteBtn.style.cssText = `
      width: 36px; height: 36px; padding: 0;
      background: rgba(3, 10, 22, 0.7); color: #9ac0dc;
      border: 1px solid rgba(0, 255, 255, 0.3); border-radius: 4px;
      font-size: 18px; cursor: pointer; transition: all 0.2s;
      backdrop-filter: blur(4px);
    `;

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.max = '100';
    slider.value = String(Math.round(audio.getMusicVolume() * 100));
    slider.style.cssText = `
      width: 80px; height: 4px; accent-color: #00ffff;
      cursor: pointer; opacity: 0.8;
    `;

    const updateIcon = () => {
      const vol = audio.getMusicVolume();
      isMuted = audio.getMasterVolume() === 0;
      if (isMuted || vol === 0) {
        muteBtn.textContent = '🔇';
      } else if (vol < 0.5) {
        muteBtn.textContent = '🔉';
      } else {
        muteBtn.textContent = '🔊';
      }
    };
    updateIcon();

    let volumeBeforeMute = audio.getMasterVolume() || 0.8;

    muteBtn.addEventListener('click', () => {
      if (isMuted) {
        audio.setMasterVolume(volumeBeforeMute);
      } else {
        volumeBeforeMute = audio.getMasterVolume();
        audio.setMasterVolume(0);
      }
      isMuted = !isMuted;
      updateIcon();
    });

    slider.addEventListener('input', () => {
      const val = parseInt(slider.value) / 100;
      audio.setMusicVolume(val);
      if (isMuted && val > 0) {
        audio.setMasterVolume(volumeBeforeMute);
        isMuted = false;
      }
      updateIcon();
    });

    muteBtn.addEventListener('mouseenter', () => {
      muteBtn.style.borderColor = 'rgba(0, 255, 255, 0.6)';
      muteBtn.style.color = '#00ffff';
    });
    muteBtn.addEventListener('mouseleave', () => {
      muteBtn.style.borderColor = 'rgba(0, 255, 255, 0.3)';
      muteBtn.style.color = '#9ac0dc';
    });

    container.appendChild(muteBtn);
    container.appendChild(slider);
    document.body.appendChild(container);
    this.audioControl = container;

    this.events.once('shutdown', () => {
      if (this.audioControl) {
        this.audioControl.remove();
        this.audioControl = null;
      }
    });
  }

  private loadRememberedEmail(): void {
    const remembered = localStorage.getItem(STORAGE_REMEMBER_CHECKED);
    const savedEmail = localStorage.getItem(STORAGE_REMEMBER_EMAIL);
    if (remembered === 'true' && savedEmail) {
      this.emailInput.value = savedEmail;
      this.rememberCheckbox.checked = true;
      this.time.delayedCall(600, () => this.passwordInput.focus());
    }
  }

  private async tryAutoLogin(): Promise<void> {
    const storedToken = localStorage.getItem('crimsonmandate_token');
    if (!storedToken) return;

    this.isLoggingIn = true;
    this.setButtonLoading(true);
    this.updateStatus('');

    try {
      const response = await fetch(`${API_URL}/profile/me`, {
        headers: { 'Authorization': `Bearer ${storedToken}` },
      });

      if (!response.ok) {
        // Token expired or invalid — clear it and show login form
        localStorage.removeItem('crimsonmandate_token');
        this.isLoggingIn = false;
        this.setButtonLoading(false);
        return;
      }

      const result = await response.json();
      if (!result.success || !result.data?.userId) {
        localStorage.removeItem('crimsonmandate_token');
        this.isLoggingIn = false;
        this.setButtonLoading(false);
        return;
      }

      console.log('[MMOLogin] Auto-login with stored token, userId:', result.data.userId);
      this.sessionToken = storedToken;
      if (result.data.displayName) {
        localStorage.setItem('crimsonmandate_display_name', result.data.displayName);
      }
      this.mmoState.initialize(result.data.userId, 1);

      EventBus.on(MMOEvents.ERROR, this.onError, this);
      this.armConnectionWatchdog();
      this.mmoNetwork.connect(storedToken);
    } catch {
      // Network error — let user login manually
      this.isLoggingIn = false;
      this.setButtonLoading(false);
    }
  }

  private saveRememberedEmail(): void {
    if (this.rememberCheckbox.checked) {
      localStorage.setItem(STORAGE_REMEMBER_EMAIL, this.emailInput.value.trim());
      localStorage.setItem(STORAGE_REMEMBER_CHECKED, 'true');
    } else {
      localStorage.removeItem(STORAGE_REMEMBER_EMAIL);
      localStorage.setItem(STORAGE_REMEMBER_CHECKED, 'false');
    }
  }

  private setButtonLoading(loading: boolean): void {
    if (loading) {
      this.loginButton.innerHTML = I18n.t('login.connecting');
      this.loginButton.style.animation = 'connectingPulse 1s ease-in-out infinite';
      this.loginButton.style.cursor = 'wait';
      this.loginButton.style.background = 'rgba(0, 80, 100, 0.9)';
    } else {
      this.loginButton.innerHTML = this.originalButtonText;
      this.loginButton.style.animation = 'buttonGlowPulse 2s ease-in-out infinite';
      this.loginButton.style.cursor = 'pointer';
      this.loginButton.style.background = 'rgba(0, 50, 70, 0.9)';
    }
  }

  private setupEventListeners(): void {
    EventBus.on(MMOEvents.CONNECTED, this.onConnected, this);
    EventBus.on(MMOEvents.AUTHENTICATED, this.onAuthenticated, this);
    EventBus.on(MMOEvents.WORLD_STATE, this.onWorldJoined, this);
    EventBus.on(MMOEvents.DISCONNECTED, this.onDisconnected, this);
    EventBus.on(MMOEvents.RECONNECT_FAILED, this.onError, this);
  }

  private removeEventListeners(): void {
    EventBus.off(MMOEvents.CONNECTED, this.onConnected, this);
    EventBus.off(MMOEvents.AUTHENTICATED, this.onAuthenticated, this);
    EventBus.off(MMOEvents.WORLD_STATE, this.onWorldJoined, this);
    EventBus.off(MMOEvents.ERROR, this.onError, this);
    EventBus.off(MMOEvents.DISCONNECTED, this.onDisconnected, this);
    EventBus.off(MMOEvents.RECONNECT_FAILED, this.onError, this);
    this.clearConnectionWatchdog();
  }

  /**
   * Arm a watchdog so a silently-dropped WebSocket (cellular hiccup,
   * iOS Safari tab suspend) doesn't leave the user stuck on "Connecting..." forever.
   * During load spikes, transient DISCONNECTED events are allowed to auto-reconnect.
   */
  private armConnectionWatchdog(): void {
    this.clearConnectionWatchdog();
    this.connectionWatchdog = this.time.delayedCall(CONNECTION_WATCHDOG_MS, () => {
      this.onError(new Error('Connection timed out'));
    });
  }

  private clearConnectionWatchdog(): void {
    if (this.connectionWatchdog) {
      this.connectionWatchdog.remove();
      this.connectionWatchdog = null;
    }
  }

  private async handleLogin(): Promise<void> {
    const email = this.emailInput.value.trim();
    const password = this.passwordInput.value.trim();

    if (!email || !password) {
      this.updateStatus(I18n.t('login.enter_email_password'));
      return;
    }

    this.isLoggingIn = true;
    this.setButtonLoading(true);
    this.saveRememberedEmail();

    try {
      await this.performLogin(email, password);
    } catch (error) {
      console.error('[MMOLogin] Login failed:', error);
      this.updateStatus(`${I18n.t('login.login_failed')}: ${error instanceof Error ? error.message : I18n.t('login.unknown_error')}`);
      this.isLoggingIn = false;
      this.setButtonLoading(false);
    }
  }

  private async performLogin(email: string, password: string): Promise<void> {
    let response: Response;
    try {
      response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, locale: localStorage.getItem('crimson_mandate_locale') || 'en' }),
      });
    } catch (_fetchError) {
      throw new Error(I18n.t('login.server_unavailable'));
    }

    let result: any;
    try {
      result = await response.json();
    } catch (_jsonError) {
      throw new Error(I18n.t('login.server_unavailable'));
    }

    if (!response.ok) {
      throw new Error(result.error?.message || `${I18n.t('login.login_failed')}: ${response.status}`);
    }

    if (!result.success || !result.data) {
      throw new Error(result.error?.message || I18n.t('login.login_failed'));
    }

    this.sessionToken = result.data.token;
    const userId = result.data.userId;
    console.log('[MMOLogin] Login successful, userId:', userId);

    // Persist token + display name so UI dialogs and auto-login can use it
    localStorage.setItem('crimsonmandate_token', this.sessionToken!);
    if (result.data.displayName) {
      localStorage.setItem('crimsonmandate_display_name', result.data.displayName);
    }

    this.mmoState.initialize(userId, 1);

    if (this.sessionToken) {
      EventBus.on(MMOEvents.ERROR, this.onError, this);
      this.armConnectionWatchdog();
      this.mmoNetwork.connect(this.sessionToken);
    }
  }

  private onConnected(): void {
    console.log('[MMOLogin] Connected to WebSocket');
    this.updateStatus(I18n.t('login.connecting'));
  }

  private onAuthenticated(payload: any): void {
    console.log('[MMOLogin] Authenticated:', payload);
    this.mmoNetwork.sendJoinWorld(1);
  }

  private onDisconnected(): void {
    if (!this.isLoggingIn || this.isTransitioning) return;
    console.warn('[MMOLogin] WebSocket dropped during login handoff; waiting for reconnect');
    this.updateStatus(I18n.t('login.connecting'));
  }

  private onWorldJoined(payload: any): void {
    if (this.isTransitioning) return;
    this.isTransitioning = true;

    console.log('[MMOLogin] Successfully joined world');

    this.mmoState.setWorldState(payload);
    this.removeEventListeners();

    const game = this.scene.manager.game;

    // Pick HUD scene based on platform (mobile vs desktop).
    // Respects ?mobile=1 / ?desktop=1 URL override and localStorage preference.
    const hudSceneKey = getHUDSceneKey();
    console.log(`[MMOLogin] Launching HUD scene: ${hudSceneKey}`);

    // If scene is no longer active (e.g. Vite HMR reloaded the page),
    // skip animations and transition immediately
    if (!this.scene.isActive()) {
      console.warn('[MMOLogin] Scene inactive during world join — direct transition');
      this.cleanupInputs();
      game.scene.stop('MMOLoginScene');
      game.scene.start('MMOGameScene');
      game.scene.start(hudSceneKey);
      return;
    }

    this.time.delayedCall(500, () => {
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.time.delayedCall(300, () => {
        this.cleanupInputs();
        game.scene.stop('MMOLoginScene');
        game.scene.start('MMOGameScene');
        game.scene.start(hudSceneKey);
      });
    });
  }

  private onError(error: any): void {
    console.error('[MMOLogin] Error:', error);
    const errorMsg = error ? error.message || error.code || I18n.t('login.connection_failed') : I18n.t('login.server_unavailable');
    this.updateStatus(`Error: ${errorMsg}`);
    this.isLoggingIn = false;
    this.setButtonLoading(false);
    // Clear watchdog + listeners so a delayed reconnect or stale event can't
    // re-enter the success path and double-fire the scene transition.
    this.removeEventListeners();
  }

  private updateStatus(message: string): void {
    if (!this.statusElement) return;
    if (message) {
      this.statusElement.textContent = message;
      this.statusElement.style.display = 'block';
      if (message.toLowerCase().includes('error') || message.toLowerCase().includes('failed')) {
        this.statusElement.style.color = '#ff6666';
      } else {
        this.statusElement.style.color = '#00ffff';
      }
    } else {
      this.statusElement.style.display = 'none';
    }
  }

  private cleanupInputs(): void {
    try {
      if (this.inputContainer?.parentNode) {
        this.inputContainer.parentNode.removeChild(this.inputContainer);
      }
    } catch (_) { /* browser extension may have already manipulated the DOM */ }
    try {
      if (this.logoContainer?.parentNode) {
        this.logoContainer.parentNode.removeChild(this.logoContainer);
        this.logoContainer = null;
      }
    } catch (_) { /* browser extension may have already manipulated the DOM */ }
    try {
      if (this.audioControl?.parentNode) {
        this.audioControl.parentNode.removeChild(this.audioControl);
        this.audioControl = null;
      }
    } catch (_) { /* browser extension may have already manipulated the DOM */ }
  }

  shutdown(): void {
    this.events.off('update', this.updateParallax, this);
    this.removeEventListeners();
    this.cleanupInputs();
  }
}
